"use client";

import { useState, useRef } from "react";
import type {
  CoinbaseConnectionState,
  CoinbaseEnvironment,
  CoinbaseOrderPreview,
  CoinbaseOrderSide,
  CoinbaseOrderSummary,
  CoinbaseProductSearchResult,
} from "@/lib/coinbase";
import { usePreviewExpiry } from "./use-preview-expiry";
import { useDialogFocus } from "./use-dialog-focus";
import { useConfirmation } from "./use-confirmation";
import { apiFetch } from "@/lib/client-api";

type CoinbaseTradeTicketProps = {
  environment: CoinbaseEnvironment;
  connection: CoinbaseConnectionState;
  onCompleted: () => Promise<void>;
};

type OrderDraft = {
  productId: string;
  side: CoinbaseOrderSide;
  baseSize: string;
  limitPrice: string;
  postOnly: boolean;
};

type PreviewIntent = {
  id: string;
  environment: CoinbaseEnvironment;
  kind: "create_order" | "cancel_order";
  state: string;
  expiresAt: string;
  preview: CoinbaseOrderPreview | CoinbaseOrderSummary;
  confirmationText: string;
};

function blankOrder(): OrderDraft {
  return {
    productId: "",
    side: "BUY",
    baseSize: "0.001",
    limitPrice: "",
    postOnly: false,
  };
}

function money(value: number | null, digits = 2) {
  if (value === null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function number(value: number | null, digits = 8) {
  return value === null ? "—" : new Intl.NumberFormat("en-US", {
    maximumFractionDigits: digits,
  }).format(value);
}

async function postOrderAction(body: unknown) {
  const response = await apiFetch("/api/coinbase/orders", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as {
    intent?: PreviewIntent;
    result?: Record<string, unknown>;
    state?: string;
    error?: string;
    code?: string;
  };
  if (!response.ok) {
    const error = new Error(payload.error ?? "The Coinbase request failed.");
    Object.assign(error, { code: payload.code, state: payload.state });
    throw error;
  }
  return payload;
}

export default function CoinbaseTradeTicket({
  environment,
  connection,
  onCompleted,
}: CoinbaseTradeTicketProps) {
  const [open, setOpen] = useState(false);
  const ticketRef = useRef<HTMLDivElement>(null);
  const { confirm, confirmationDialog } = useConfirmation();
  const [draft, setDraft] = useState<OrderDraft>(blankOrder);
  const [previewIntent, setPreviewIntent] = useState<PreviewIntent | null>(null);
  const previewExpired = usePreviewExpiry(previewIntent?.expiresAt);
  const [confirmation, setConfirmation] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [productResults, setProductResults] = useState<CoinbaseProductSearchResult[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const label = environment === "paper" ? "Paper" : "Real";

  useDialogFocus(open, ticketRef, () => { if (!busy) closeTicket(); });

  function setField<Key extends keyof OrderDraft>(key: Key, value: OrderDraft[Key]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setPreviewIntent(null);
    setConfirmation("");
    setAuthorized(false);
    setResult(null);
    if (key === "productId") setProductResults([]);
  }

  function closeTicket() {
    setOpen(false);
    setDraft(blankOrder());
    setPreviewIntent(null);
    setConfirmation("");
    setAuthorized(false);
    setError(null);
    setResult(null);
    setProductResults([]);
  }

  async function searchProducts() {
    const query = draft.productId.trim();
    if (query.length < 2) {
      setError("Enter at least two letters or a Coinbase product id to search.");
      return;
    }
    setSearchingProducts(true);
    setError(null);
    try {
      const response = await apiFetch(
        `/api/coinbase/products?q=${encodeURIComponent(query)}`,
        { cache: "no-store", headers: { accept: "application/json" } },
      );
      const payload = await response.json() as {
        products?: CoinbaseProductSearchResult[];
        error?: string;
      };
      if (!response.ok || !payload.products) {
        throw new Error(payload.error ?? "Coinbase product search failed.");
      }
      setProductResults(payload.products);
      if (!payload.products.length) setError(`No tradable Coinbase products matched “${query}”.`);
    } catch (searchError) {
      setError(searchError instanceof Error ? searchError.message : "Coinbase product search failed.");
    } finally {
      setSearchingProducts(false);
    }
  }

  function selectProduct(product: CoinbaseProductSearchResult) {
    const referencePrice = draft.side === "BUY"
      ? product.bestAsk ?? product.price ?? product.bestBid
      : product.bestBid ?? product.price ?? product.bestAsk;
    setDraft((current) => ({
      ...current,
      productId: product.productId,
      limitPrice: referencePrice === null ? current.limitPrice : String(referencePrice),
    }));
    setProductResults([]);
    setPreviewIntent(null);
    setConfirmation("");
    setAuthorized(false);
    setResult(null);
  }

  async function previewOrder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const payload = await postOrderAction({
        action: "preview_create",
        order: { environment, ...draft },
      });
      if (!payload.intent) throw new Error("Coinbase did not return an order preview.");
      setPreviewIntent(payload.intent);
      setConfirmation("");
      setAuthorized(false);
    } catch (previewError) {
      setError(previewError instanceof Error ? previewError.message : "The order could not be previewed.");
    } finally {
      setBusy(false);
    }
  }

  async function executeOrder() {
    if (!previewIntent || previewExpired || !connection.ordersEnabled || !authorized || confirmation !== previewIntent.confirmationText) return;
    const preview = previewIntent.preview as CoinbaseOrderPreview;
    if (!await confirm(
      `${previewIntent.confirmationText}\n\n${preview.productId} · ${preview.sideLabel}\n${preview.baseSize} at ${money(Number(preview.limitPrice), 8)}\nMaximum notional ${money(preview.maximumCapitalAtRisk)}\n\nSubmit this exact order?`,
    )) return;
    if (Date.parse(previewIntent.expiresAt) <= Date.now()) { setError("This preview expired. Preview the order again."); return; }
    setBusy(true);
    setError(null);
    try {
      const payload = await postOrderAction({
        action: "execute",
        intentId: previewIntent.id,
        confirmation,
      });
      const orderId = typeof payload.result?.order_id === "string" ? payload.result.order_id : "recorded";
      const filled = typeof payload.result?.filled_size === "string" ? payload.result.filled_size : null;
      const remaining = typeof payload.result?.remaining_size === "string" ? payload.result.remaining_size : null;
      setResult(`Order ${orderId}${filled ? ` · ${filled} filled` : ""}${remaining ? ` · ${remaining} resting` : ""}`);
      setPreviewIntent(null);
      setConfirmation("");
      setAuthorized(false);
      await onCompleted();
    } catch (executeError) {
      setError(executeError instanceof Error ? executeError.message : "The order result is unavailable.");
    } finally {
      setBusy(false);
    }
  }

  async function cancelOrder(order: CoinbaseOrderSummary) {
    if (!await confirm(
      `Prepare cancellation for ${order.productId} order ${order.orderId}?\n\nThis will require one final confirmation before Coinbase receives it.`,
    )) return;
    setCancelingId(order.orderId);
    setError(null);
    setResult(null);
    try {
      const previewPayload = await postOrderAction({
        action: "preview_cancel",
        environment,
        orderId: order.orderId,
      });
      if (!previewPayload.intent) throw new Error("Coinbase did not return a cancellation preview.");
      if (!await confirm(
        `${previewPayload.intent.confirmationText}\n\n${order.productId} · ${number(order.remainingSize)} remaining\n\nCancel this open order now?`,
      )) return;
      await postOrderAction({
        action: "execute",
        intentId: previewPayload.intent.id,
        confirmation: previewPayload.intent.confirmationText,
      });
      setResult(`Canceled open quantity from ${order.orderId}.`);
      await onCompleted();
    } catch (cancelError) {
      setError(cancelError instanceof Error ? cancelError.message : "The cancellation result is unavailable.");
    } finally {
      setCancelingId(null);
    }
  }

  const preview = previewIntent?.kind === "create_order"
    ? previewIntent.preview as CoinbaseOrderPreview
    : null;

  return (
    <section className="kalshi-console coinbase-console" aria-label={`Coinbase ${label} trading`}>
      <header>
        <div>
          <span>Coinbase · {label}</span>
          <strong>{connection.status === "live" ? connection.message : `${label} account not connected`}</strong>
        </div>
        {connection.ordersEnabled ? (
          <button type="button" onClick={() => open ? closeTicket() : setOpen(true)}>
            {open ? "Close ticket" : "New order"}
          </button>
        ) : environment === "production" ? (
          <a href="https://portal.cdp.coinbase.com/access/api" target="_blank" rel="noreferrer">
            {connection.canTrade ? "Order gate off" : "Enable trade key"}
          </a>
        ) : (
          <button type="button" disabled>Paper unavailable</button>
        )}
      </header>

      <details className="connection-details"><summary>Connection details</summary><p>{connection.message}</p><dl className="kalshi-account-line">
        <div><dt>Available USD</dt><dd>{money(connection.availableBalance)}</dd></div>
        <div><dt>Portfolio</dt><dd>{money(connection.portfolioValue)}</dd></div>
        <div><dt>Open orders</dt><dd>{connection.openOrderCount}</dd></div>
        <div><dt>Per-order cap</dt><dd>{money(connection.maximumOrderUsd, 0)}</dd></div>
      </dl></details>

      {open && connection.ordersEnabled && (
        <div className="ticket-overlay"><button type="button" className="backdrop-dismiss" aria-label="Close Coinbase order" disabled={busy} onClick={closeTicket}/><div ref={ticketRef} role="dialog" aria-modal="true" aria-label={`Coinbase ${label.toLowerCase()} order`} className="ticket-dialog"><header className="ticket-dialog-header"><div><h2>Coinbase</h2><span>{label} order</span></div><button type="button" disabled={busy} onClick={closeTicket}>Close</button></header><form className="kalshi-ticket" onSubmit={(event) => void previewOrder(event)}>
          <div className="kalshi-ticket-grid">
            <label>
              <span>Spot product</span>
              <div className="kalshi-ticker-input">
                <input
                  required
                  maxLength={120}
                  value={draft.productId}
                  onChange={(event) => setField("productId", event.target.value.toUpperCase())}
                  placeholder="BTC or BTC-USD"
                />
                <button type="button" onClick={() => void searchProducts()} disabled={searchingProducts}>
                  {searchingProducts ? "Searching" : "Find"}
                </button>
              </div>
            </label>
            <label>
              <span>Side</span>
              <select value={draft.side} onChange={(event) => setField("side", event.target.value as CoinbaseOrderSide)}>
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
              </select>
            </label>
            <label>
              <span>Limit price</span>
              <input
                required
                min="0.00000001"
                step="any"
                type="number"
                value={draft.limitPrice}
                onChange={(event) => setField("limitPrice", event.target.value)}
              />
            </label>
            <label>
              <span>Base size</span>
              <input
                required
                min="0.00000001"
                step="any"
                type="number"
                value={draft.baseSize}
                onChange={(event) => setField("baseSize", event.target.value)}
              />
            </label>
          </div>

          {productResults.length > 0 && (
            <div className="kalshi-market-results" role="list" aria-label="Tradable Coinbase product matches">
              {productResults.map((product) => (
                <button key={product.productId} type="button" onClick={() => selectProduct(product)}>
                  <span>{product.productId}</span>
                  <strong>{product.displayName}</strong>
                  <small>Ask {money(product.bestAsk ?? product.price, 8)}</small>
                </button>
              ))}
            </div>
          )}

          <details className="kalshi-advanced">
            <summary>Execution controls</summary>
            <label>
              <input
                type="checkbox"
                checked={draft.postOnly}
                onChange={(event) => setField("postOnly", event.target.checked)}
              />
              Post only
            </label>
            <span>{environment === "paper"
              ? "Paper fills use observed top-of-book prices; fees, queue position, and partial liquidity are not modeled."
              : "Limit GTC only. Coinbase previews fees before the single permitted submission."}</span>
          </details>

          {!preview && (
            <footer>
              <span>No order is sent while previewing.</span>
              <button type="submit" disabled={busy}>{busy ? "Checking" : `Review ${label.toLowerCase()} order`}</button>
            </footer>
          )}

          {preview && (
            <div className="kalshi-preview">
              <header>
                <div>
                  <span>Single-use preview · expires in five minutes</span>
                  <strong>{preview.displayName}</strong>
                </div>
                <button type="button" onClick={() => setPreviewIntent(null)}>Revise</button>
              </header>
              <dl>
                <div><dt>Instruction</dt><dd>{preview.sideLabel}</dd></div>
                <div><dt>Limit</dt><dd>{money(Number(preview.limitPrice), 8)}</dd></div>
                <div><dt>Base size</dt><dd>{preview.baseSize}</dd></div>
                <div><dt>Max notional</dt><dd>{money(preview.maximumCapitalAtRisk)}</dd></div>
                <div><dt>Best bid</dt><dd>{money(preview.bestBid, 8)}</dd></div>
                <div><dt>Best ask</dt><dd>{money(preview.bestAsk, 8)}</dd></div>
                <div><dt>Fee preview</dt><dd>{money(preview.commissionTotal)}</dd></div>
                <div><dt>Post only</dt><dd>{preview.postOnly ? "Yes" : "No"}</dd></div>
              </dl>
              <p>
                {preview.likelyMarketable === null
                  ? "No complete top-of-book comparison is available."
                  : preview.likelyMarketable
                    ? "This limit appears marketable and may fill immediately."
                    : "This limit is not currently marketable and may rest."}
                {preview.previewWarnings.length ? ` ${preview.previewWarnings.join(" ")}` : ""}
              </p>
              {previewExpired && <p role="status">This preview expired. Preview the order again.</p>}
              <label className="kalshi-confirmation">
                <span>Type {previewIntent!.confirmationText}</span>
                <input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="kalshi-authorize">
                <input
                  type="checkbox"
                  checked={authorized}
                  onChange={(event) => setAuthorized(event.target.checked)}
                />
                I authorize this exact {label.toLowerCase()} order. No agent may submit it for me.
              </label>
              <footer>
                <span>One submission · no automatic retry</span>
                <button
                  type="button"
                  disabled={busy || previewExpired || !connection.ordersEnabled || !authorized || confirmation !== previewIntent!.confirmationText}
                  onClick={() => void executeOrder()}
                >
                  {busy ? "Submitting once" : `Place ${label.toLowerCase()} order`}
                </button>
              </footer>
            </div>
          )}
        </form>{error && <p className="kalshi-message error" role="alert">{error}</p>}</div></div>
      )}

      {connection.openOrders.length > 0 && (
        <details className="kalshi-open-orders">
          <summary>{connection.openOrders.length} open Coinbase orders</summary>
          {connection.openOrders.map((order) => (
            <div key={order.orderId}>
              <span>{order.productId}</span>
              <span>{order.side} · {money(order.limitPrice, 8)}</span>
              <span>{number(order.remainingSize)} remaining</span>
              <button
                type="button"
                disabled={cancelingId !== null || !connection.ordersEnabled}
                onClick={() => void cancelOrder(order)}
              >
                {cancelingId === order.orderId ? "Confirming" : "Cancel"}
              </button>
            </div>
          ))}
        </details>
      )}

      {!open && (error || result) && (
        <p className={error ? "kalshi-message error" : "kalshi-message"} role={error ? "alert" : "status"}>
          {error ?? result}
        </p>
      )}
      {confirmationDialog}
    </section>
  );
}
